<template>
  <div class="mainDiv">
    <div class="container">
      <div class="wrapper" v-if="loading || eventsIncoming">
        <LoadingWheel></LoadingWheel>
      </div>
      <div class="wrapper" v-else-if="eventsReversed.length > 0" ref="eventList">
        <div class="event-row" :class="{
          currentEvent: eventInRange(event.data.s, event.data.e),
          almostCurrentEvent: eventInRange(event.data.s - 5000, event.data.e + 5000),
          enabled: isEnabled(event),
          'anim-enabled': eventsReversed.length < 150,
          }"
        :key="event.data.s" v-for="event in eventsData">
          <div class="card" @touchstart="nothing"
        :key="event.data.s" @click="selectEvent(event)"
        v-longpress="() => $emit('openClipMaker', event)"
        :id="`event-${event.data.s}`">
        <div class="icons">
            <div class="icon" :class="event | eventType"></div>
            <template v-if="event.data.c">
              <div v-if="Object.keys(event.data.c).length > 1"
                class="icon subicon" :class="event | subEventType"></div>
            </template>
              </div>
          <h2>{{event | date}}</h2>
          <h3> {{ event | duration }} </h3>
        </div>
        </div>
        <button class="blank-clip-btn" @click="$emit('createBlankClip')">Create a clip</button>
      </div>
      <div class="wrapper" v-else>
        <div class="event-row enabled">
          <div class="card no-events">
            <h2>No events available</h2>
          </div>
        </div>
        <br />
        <button class="blank-clip-btn" @click="$emit('createBlankClip')">Create a clip</button>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingWheel from './LoadingWheel.vue';

export default {
  components: { LoadingWheel },
  props: {
    videoDate: Number,
    eventsEnabled: {},
  },
  data() {
    return {
      dayOffset: -1,
      eventDate: null,
      previousEventDate: null,
      filterDebounceFn: null,
      loading: false,
      eventsIncoming: null,
    };
  },
  filters: {
    date(event) {
      if (event.data.s === 'No events available') {
        return event.data.s;
      }
      return new Date(event.data.s).toLocaleString('en-GB', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    },
    duration(event) {
      const start = event.data.s;
      const end = event.data.e;
      let milliseconds = end - start;

      const components = [];
      const days = Math.floor(milliseconds / (24 * 60 * 60 * 1000));
      if (days > 0) {
        components.push(`${days}d`);
        milliseconds -= days * 24 * 60 * 60 * 1000;
      }

      // Add hours to the components if applicable
      const hours = Math.floor(milliseconds / (60 * 60 * 1000));
      if (hours > 0) {
        components.push(`${hours}h`);
        milliseconds -= hours * 60 * 60 * 1000;
      }

      // Add minutes to the components if applicable
      const minutes = Math.floor(milliseconds / (60 * 1000));
      if (minutes > 0) {
        components.push(`${minutes}m`);
        milliseconds -= minutes * 60 * 1000;
      }

      // Add seconds to the components if applicable
      const seconds = Math.floor(milliseconds / 1000);
      if (seconds > 0) {
        components.push(`${seconds % 60}s`);
      }

      // If no components were added, return "0s"
      if (components.length === 0) {
        return '0s';
      }

      // Combine the components into a single string
      if (components.length === 1) {
        return components[0];
      }
      return components.join(' ');
    },
    eventType(event) {
      switch (event.data.t.s) {
        case 'e':
          return 'external';
        case 'm':
          return 'motion';
        case 'v':
          return 'car';
        case 'p':
          return 'human';
        default:
          return 'generic';
      }
    },
    subEventType(event) {
      const keys = Object.keys(event.data.c).filter((k) => k !== event.data.t.s);
      switch (keys[0]) {
        case 'e':
          return 'external';
        case 'm':
          return 'motion';
        case 'v':
          return 'car';
        case 'p':
          return 'human';
        default:
          return 'generic';
      }
    },
  },
  watch: {
    filterBy(newStart) {
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      const offset = Math.floor((newStart - todayMidnight.getTime()) / 86400000);
      this.dayOffset = offset;
    },
    eventsReversed: {
      handler() {
        if (this.eventsIncoming) clearTimeout(this.eventsIncoming);
        this.eventsIncoming = setTimeout(() => {
          this.eventsIncoming = null;
        }, 300);
      },
      deep: true,
    },
  },
  methods: {
    nothing() {
    },
    yesterday() {
      this.dayOffset -= 1;
    },
    today() {
      this.dayOffset = 0;
    },
    tomorrow() {
      if (this.dayOffset < 0) {
        this.dayOffset += 1;
      }
    },
    openDatePicker() {
      this.$refs.dateInput.focus({ preventScroll: true });
      if (this.$refs.dateInput.showPicker) this.$refs.dateInput.showPicker();
      else this.$refs.dateInput.click();
    },
    onDateSelected(event) {
      const { target } = event;
      const selected = target.value; // YYYY-MM-DD
      if (!selected) return;

      const [y, m, d] = selected.split('-').map(Number);
      const selectedDate = new Date(y, m - 1, d);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const selectedStart = Math.min(selectedDate.getTime(), todayStart.getTime());
      const base = new Date();
      base.setHours(0, 0, 0, 0);
      const currentStart = base.getTime() + this.dayOffset * 86400000;
      if (selectedStart === currentStart) return;

      const offset = Math.round((selectedStart - todayStart.getTime()) / 86400000);
      this.dayOffset = offset;
      target.value = null;
    },
    selectEvent(event) {
      if (event.data.s === 'No events available') {
        return;
      }
      this.$store.dispatch('selectEvent', event.data.s);
      setTimeout(() => {
        this.$store.dispatch('selectEvent', null);
      }, 100);
    },
    async refreshEvents() {
      this.$store.dispatch('changeEvents', null);
      this.$emit('refreshEvents');
    },
    scrollToEvent(ts) {
      const id = `event-${ts}`;
      const el = document.getElementById(id);
      const container = this.$refs.eventList;
      if (!el || !container) return;

      // Helper: is element fully visible in container (with a small margin)
      const isVisible = () => {
        const c = container.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        const margin = 8;
        return r.top >= c.top + margin && r.bottom <= c.bottom - margin;
      };

      // Perform scroll (center element vertically within container)
      const centerScroll = () => {
        const elTop = el.offsetTop - container.offsetTop;
        const target = elTop - (container.clientHeight / 2 - el.offsetHeight / 2);
        container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
      };

      // If not visible, center it
      if (!isVisible()) {
        // Ensure layout is stable before measuring/scrolling
        this.$nextTick(() => {
          if (!isVisible()) centerScroll();
        });
      }
    },
    eventInRange(start, end) {
      if (start < this.videoDate && this.videoDate < end) return true;
      return false;
    },
    isEnabled(event) {
      if (!this.eventsEnabled) return true;
      if (!Object.keys(this.eventsEnabled).length) return true;
      if (Object.values(this.eventsEnabled).every((v) => !v)) return true;
      // At least one filter on at this point
      if (!event.data) return false;
      if (event.data.c) {
        return Object.keys(this.eventsEnabled)
          .every((k) => (!this.eventsEnabled[k]) || (k in event.data.c));
      }
      return this.eventsEnabled[event.data.t.s] === true;
    },
  },

  created() {
    this.dayOffset = 0;
    this.$store.dispatch('ChangeFilter', 3600000);
    this.$store.dispatch('changeEndTime', new Date().getTime());
    this.$store.dispatch('changeStartTime', new Date().setHours(0, 0, 0, 0));
    this.$store.dispatch('changeEvents', null);
    this.$nextTick(() => {
      this.$emit('refreshEvents');
    });
  },

  computed: {
    eventsReversed() {
      return this.$store.getters.eventsReverse;
    },
    eventsData() {
      if (this.eventsReversed && this.eventsReversed.length > 0) {
        return this.eventsReversed;
      } return [{
        data: {
          s: 'No events available',
          t: {
            p: 20,
            v: 10,
          },
        },
      }];
    },
    filterBy() {
      return this.$store.getters.filterTime;
    },
    filterTo() {
      return this.$store.getters.eventTo;
    },
    selectedEvent() {
      return this.$store.getters.selectedEvent;
    },
    googleAuthentiated() {
      return this.$store.state.googleAuth.authenticatedWithGoogle;
    },
    todayYMD() {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`; // always local date, always YYYY-MM-DD
    },
  },
  destroyed() {
    this.$store.dispatch('changeEvents', null);
    this.$store.dispatch('ChangeFilter', 3600000);
  },
};
</script>

<style scoped>
h2 {
  font-size: 16px;
}
.fade-enter-active, .fade-leave-active {
  transition: all .3s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  transform: translateY(-14px);
}
.time {
  padding-left: 18px;
  text-align: left;
  width: 100%;
  height: 46px;
  background-color: var(--surface);
  font-size: 18px;
}
.dateSelect {
font-size: 18px;
margin: 8px;
padding: 11px;
height: 44px;
background: var(--surface);
border-radius: var(--border-radius);
margin-bottom: 14px;
text-align: left;
display: inline-block;
}

.dateSelect.refreshEvents {
  background-image: url("data:image/svg+xml,%0A%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 2C8.02093 2 6.20188 2.72229 4.80469 3.91667C4.73465 3.97242 4.67657 4.0417 4.63388 4.12039C4.5912 4.19907 4.5648 4.28554 4.55626 4.37465C4.54771 4.46376 4.5572 4.55367 4.58415 4.63903C4.6111 4.72439 4.65497 4.80345 4.71313 4.8715C4.77129 4.93954 4.84256 4.99518 4.92268 5.03509C5.00281 5.075 5.09015 5.09837 5.1795 5.1038C5.26885 5.10923 5.35838 5.09661 5.44275 5.0667C5.52712 5.03678 5.6046 4.99019 5.67057 4.92969C6.83605 3.9334 8.3444 3.33333 10 3.33333C13.4644 3.33333 16.3007 5.95862 16.6341 9.33333H14.6667L17.3333 13.3333L20 9.33333H17.9661C17.6253 5.23444 14.1852 2 10 2ZM2.66667 6.66667L0 10.6667H2.03385C2.37472 14.7656 5.81477 18 10 18C11.9791 18 13.7981 17.2777 15.1953 16.0833C15.2653 16.0276 15.3234 15.9583 15.3661 15.8796C15.4088 15.8009 15.4352 15.7145 15.4437 15.6253C15.4523 15.5362 15.4428 15.4463 15.4159 15.361C15.3889 15.2756 15.345 15.1965 15.2869 15.1285C15.2287 15.0605 15.1574 15.0048 15.0773 14.9649C14.9972 14.925 14.9099 14.9016 14.8205 14.8962C14.7312 14.8908 14.6416 14.9034 14.5573 14.9333C14.4729 14.9632 14.3954 15.0098 14.3294 15.0703C13.164 16.0666 11.6556 16.6667 10 16.6667C6.53563 16.6667 3.69934 14.0414 3.36589 10.6667H5.33333L2.66667 6.66667Z' fill='white'/%3E%3C/svg%3E%0A");
  background-size: 20px;
  background-repeat: no-repeat;
  background-position: center;
  width: 30px;
}

.mainDiv {
  display: flex;
  flex-direction: column;
}

.container {
  position: relative;
  width: 321px;
  display: flex;
  align-items: center;
  flex-direction: column;
  flex: 1 1 20vh;
}

.human {
background-image: url("data:image/svg+xml,%3Csvg width='34' height='25' viewBox='0 0 34 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.488647 0.231934V2.48193C0.488647 8.70693 4.04621 14.1519 9.34231 17.1819V24.2319H33.4886V21.2319C33.4886 17.2419 24.9086 15.2319 20.6106 15.2319H20.2082C12.5618 15.2319 6.12279 9.23193 6.12279 2.48193V0.231934H0.488647ZM20.6106 0.231934C18.9029 0.231934 17.2651 0.864074 16.0575 1.98929C14.85 3.11451 14.1716 4.64063 14.1716 6.23193C14.1716 7.82323 14.85 9.34936 16.0575 10.4746C17.2651 11.5998 18.9029 12.2319 20.6106 12.2319C22.3183 12.2319 23.9561 11.5998 25.1637 10.4746C26.3712 9.34936 27.0496 7.82323 27.0496 6.23193C27.0496 4.64063 26.3712 3.11451 25.1637 1.98929C23.9561 0.864074 22.3183 0.231934 20.6106 0.231934Z' fill='%23EAEBEC'/%3E%3C/svg%3E%0A");}
.car {
background-image: url("data:image/svg+xml,%3Csvg width='34' height='29' viewBox='0 0 34 29' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M31.1968 10.2336L28.9396 3.84963C28.6122 2.92015 27.9824 2.1116 27.1397 1.53886C26.297 0.966116 25.2842 0.658311 24.2454 0.659182H9.20067C8.16181 0.658311 7.14909 0.966116 6.30638 1.53886C5.46368 2.1116 4.83386 2.92015 4.50642 3.84963L2.24922 10.2336C1.64899 10.4704 1.1364 10.8692 0.775883 11.3798C0.415368 11.8905 0.223022 12.4902 0.223022 13.1036V20.8814C0.223022 22.0294 0.889622 23.0218 1.87302 23.5616V27.1036C1.87302 27.5162 2.04686 27.9118 2.3563 28.2036C2.66573 28.4953 3.08542 28.6592 3.52302 28.6592H5.17302C5.61063 28.6592 6.03031 28.4953 6.33975 28.2036C6.64918 27.9118 6.82302 27.5162 6.82302 27.1036V23.9925H26.623V27.1036C26.623 27.5162 26.7969 27.9118 27.1063 28.2036C27.4157 28.4953 27.8354 28.6592 28.273 28.6592H29.923C30.3606 28.6592 30.7803 28.4953 31.0897 28.2036C31.3992 27.9118 31.573 27.5162 31.573 27.1036V23.5616C32.0736 23.291 32.4897 22.9007 32.7794 22.4302C33.0691 21.9596 33.2221 21.4254 33.223 20.8814V13.1036C33.223 12.4902 33.0307 11.8905 32.6702 11.3798C32.3096 10.8692 31.7971 10.4704 31.1968 10.2336ZM9.20067 3.77029H24.2437C24.9549 3.77029 25.5852 4.19651 25.8096 4.83429L27.6345 9.99251H5.81157L7.63482 4.83429C7.7443 4.52442 7.95446 4.25489 8.2355 4.06391C8.51655 3.87294 8.85423 3.77021 9.20067 3.77029V3.77029ZM5.99802 19.3258C5.67289 19.3257 5.35097 19.2653 5.05063 19.1479C4.75029 19.0305 4.47742 18.8585 4.24759 18.6417C4.01777 18.4248 3.83549 18.1675 3.71117 17.8842C3.58685 17.601 3.52291 17.2975 3.52302 16.991C3.52313 16.6844 3.58728 16.3809 3.7118 16.0978C3.83632 15.8146 4.01878 15.5574 4.24876 15.3407C4.47874 15.124 4.75173 14.9522 5.05215 14.835C5.35257 14.7178 5.67454 14.6575 5.99967 14.6576C6.6563 14.6578 7.28595 14.9039 7.7501 15.3418C8.21426 15.7797 8.47489 16.3735 8.47467 16.9925C8.47445 17.6116 8.2134 18.2052 7.74894 18.6428C7.28447 19.0803 6.65465 19.3261 5.99802 19.3258V19.3258ZM27.448 19.3258C27.1229 19.3257 26.801 19.2653 26.5006 19.1479C26.2003 19.0305 25.9274 18.8585 25.6976 18.6417C25.4678 18.4248 25.2855 18.1675 25.1612 17.8842C25.0368 17.601 24.9729 17.2975 24.973 16.991C24.9731 16.6844 25.0373 16.3809 25.1618 16.0978C25.2863 15.8146 25.4688 15.5574 25.6988 15.3407C25.9287 15.124 26.2017 14.9522 26.5022 14.835C26.8026 14.7178 27.1245 14.6575 27.4497 14.6576C28.1063 14.6578 28.7359 14.9039 29.2001 15.3418C29.6643 15.7797 29.9249 16.3735 29.9247 16.9925C29.9245 17.6116 29.6634 18.2052 29.1989 18.6428C28.7345 19.0803 28.1047 19.3261 27.448 19.3258V19.3258Z' fill='%23EAEBEC'/%3E%3C/svg%3E%0A");}
.external {
background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="32" height="32"%3E%3Cpath fill="%23ffffff" d="M224 0c-17.7 0-32 14.3-32 32V51.2C119 66 64 130.6 64 208v25.4c0 45.4-15.5 89.5-43.8 124.9L5.3 377c-5.8 7.2-6.9 17.1-2.9 25.4S14.8 416 24 416H424c9.2 0 17.6-5.3 21.6-13.6s2.9-18.2-2.9-25.4l-14.9-18.6C399.5 322.9 384 278.8 384 233.4V208c0-77.4-55-142-128-156.8V32c0-17.7-14.3-32-32-32zm0 96c61.9 0 112 50.1 112 112v25.4c0 47.9 13.9 94.6 39.7 134.6H72.3C98.1 328 112 281.3 112 233.4V208c0-61.9 50.1-112 112-112zm64 352H224 160c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7s18.7-28.3 18.7-45.3z"%2F%3E%3C%2Fsvg%3E');}
.generic {
background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="32" height="32"%3E%3Cpath fill="%23ffffff" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"%2F%3E%3C%2Fsvg%3E');}
.motion {
background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="32" height="32"%3E%3Cpath fill="%23ffffff" d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"%2F%3E%3C%2Fsvg%3E'); }

.icons {
  --icon-size: 20px;
  --subicon-gap: 4px;
}

.icon {
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  height: var(--icon-size);
  width: var(--icon-size);;
  margin-right: calc(var(--icon-size) + var(--subicon-gap));
}
.icon:has(+ .subicon){
  margin-right: var(--subicon-gap);
}
.icon.subicon{
  margin-right: 0;
}

.icons {
  display: flex;
  flex-wrap: nowrap;
}

.card {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--surface);
  box-sizing: border-box;
  border-radius: var(--border-radius);
  transition: all 0.1s ease;
  border: var(--border);
  padding: 4px 16px;
  user-select: none;
}
.card:active {
border: var(--border);

}

.card.no-events {
  text-align: center;
  justify-content: center;
  border: var(--border);
  width: 100%;
}

.card.no-events:active {
  border: var(--border);

}

.card h3 {
  width: 65px;
  font-weight: normal;
  font-size: 1rem;
  text-align: right;
}

.wrapper {
  width: 321px;
  flex: 1 1 20vh;
  display: flex;
  overflow-y: scroll;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding-bottom: 16px;
  z-index: 25;
  mask-image: linear-gradient(
    rgba(0,0,0,0) 0px,
    rgba(0,0,0,1) 12px,
    rgba(0,0,0,1) calc(100% - 12px),
    rgba(0,0,0,0) 100%
    )
}
.wrapper::-webkit-scrollbar {
  display: none;
}

.clip-btn {
  /* margin-left: 20px; */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M14.75 7.46L12 3.93l1.97-.39l2.74 3.53l-1.96.39m6.87-1.36l-.78-3.92l-3.93.78l2.74 3.54l1.97-.4M4 20v-8h16v1.09c.72.12 1.39.37 2 .72V10H2v10a2 2 0 0 0 2 2h9.81c-.35-.61-.59-1.28-.72-2H4m7.81-11.95L9.07 4.5l-1.97.41l2.75 3.53l1.96-.39M4.16 5.5l-.98.19a1.995 1.995 0 0 0-1.57 2.35L2 10l4.9-.97L4.16 5.5M20 18v-3h-2v3h-3v2h3v3h2v-3h3v-2h-3Z'/%3E%3C/svg%3E%0A");
  height: 40px;
  width: 40px;
  background-position: center;
  background-size: 100%;
  border: none;
  background-repeat: no-repeat;
  background-color: transparent;
}

.event-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 75px;
  width: 300px;
  margin-top: 14px;
}

.anim-enabled.event-row, .anim-enabled.event-row > * {
  transition:
    height .25s linear,
    opacity .20s linear,
    margin-top .25s linear;
}

.event-row:not(.enabled), .event-row:not(.enabled) > * {
  margin-top: 0;
  height: 0;
  opacity: 0;
}

.event-row.almostCurrentEvent .card {
  border-color: var(--accent-faint);
}
.event-row.almostCurrentEvent.currentEvent .card {
  outline: 3px solid var(--accent-faint);
  border-color: var(--accent);
  color: var(--accent);
  outline-offset: -5px;
}

.event-row .card h2 {
  color: inherit;
}

.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.blank-clip-btn {
  background: var(--success);
  border-radius: var(--border-radius);
  padding: 20px;
}

.event-row+.blank-clip-btn {
  margin-top: 1rem;
}

.dateSelect.left {
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15 6L9 12L15 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-position: center;
  background-repeat: no-repeat;
  background-size: 24px;
  width: 34px;
  touch-action: manipulation;
}

.dateSelect.right {
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9 6L15 12L9 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-position: center;
  background-repeat: no-repeat;
  background-size: 24px;
  width: 34px;
  touch-action: manipulation;
}

.icons8-share {
    display: inline-block;
    width: 32px;
    height: 32px;
    background: url('~@/assets/share.svg') 50% 50% no-repeat;
    background-size: 100%;
    margin-left: 20px;
}

</style>
